import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { PhoneCall, CheckCircle2, AlertTriangle } from "lucide-react";

const PROVIDERS = [
  { key: "mtn_momo", label: "MTN MoMo", bg: "#FFCC00", text: "#1F2421" },
  { key: "telecel_cash", label: "Telecel Cash", bg: "#E20613", text: "#FFFFFF" },
  { key: "at_money", label: "AT Money", bg: "#00539B", text: "#FFFFFF" },
];

const REGIONS = ["Greater Accra", "Ashanti", "Eastern", "Western", "Central", "Volta", "Northern", "Upper East", "Upper West", "Bono", "Bono East", "Ahafo", "Oti", "Savannah", "Western North", "North East"];

export default function Checkout() {
  const { items, total, refresh } = useCart();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [provider, setProvider] = useState("mtn_momo");
  const [phone, setPhone] = useState(user?.phone || "");
  const [address, setAddress] = useState("");
  const [region, setRegion] = useState(user?.region || "Greater Accra");
  const [step, setStep] = useState("form"); // form -> prompt -> success
  const [order, setOrder] = useState(null);

  useEffect(() => { if (!loading && !user) nav("/login"); }, [loading, user, nav]);
  if (loading) return <div className="text-center py-20 text-[#5C6660]">Loading...</div>;
  if (!user) return null;
  if (items.length === 0 && step === "form") {
    return <div className="text-center py-20">Your basket is empty. <a href="/marketplace" className="text-[#C85A32] font-semibold">Shop now</a></div>;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!/^0\d{9}$/.test(phone)) {
      toast.error("Enter a valid 10-digit Ghana phone number (e.g., 024XXXXXXX)");
      return;
    }
    setStep("prompt");
    try {
      const callback_url = `${window.location.origin}/payment/callback`;
      const { data } = await api.post("/checkout", { provider, phone, delivery_address: address, delivery_region: region, callback_url });
      if (data.authorization_url) {
        // TEST MODE — hand off to Paystack hosted checkout
        window.location.href = data.authorization_url;
        return;
      }
      if (data.simulated) {
        // Simulation fallback (no PAYSTACK_SECRET_KEY set)
        setOrder(data.order);
        await refresh();
        setStep("success");
        return;
      }
      toast.error("Unexpected checkout response");
      setStep("form");
    } catch (err) {
      toast.error(formatError(err));
      setStep("form");
    }
  };

  if (step === "success" && order) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <CheckCircle2 className="w-16 h-16 mx-auto text-[#1B804E]" />
        <h1 className="text-3xl font-extrabold text-[#1E4D3B] mt-4">Payment confirmed!</h1>
        <p className="text-[#5C6660] mt-2">Your MoMo payment via {PROVIDERS.find(p => p.key === order.payment.provider)?.label} was successful.</p>
        <div className="card-farm p-6 mt-6 text-left">
          <div className="text-xs uppercase tracking-widest font-bold text-[#8C6D53]">Reference</div>
          <div className="font-mono font-bold text-lg text-[#1F2421]" data-testid="order-reference">{order.payment.reference}</div>
          <div className="h-px bg-[#E5D9C8] my-4" />
          <div className="flex justify-between font-bold text-[#1E4D3B]"><span>Total paid</span><span>GH₵ {order.total.toFixed(2)}</span></div>
        </div>
        <div className="flex gap-3 mt-6 justify-center">
          <button onClick={() => nav("/orders")} className="btn-primary px-5 py-3 rounded-xl" data-testid="checkout-view-orders">View my orders</button>
          <button onClick={() => nav("/marketplace")} className="btn-outline-forest px-5 py-3 rounded-xl" data-testid="checkout-continue-shopping">Continue shopping</button>
        </div>
      </div>
    );
  }

  if (step === "prompt") {
    const prov = PROVIDERS.find(p => p.key === provider);
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center" style={{ background: prov.bg, color: prov.text }}>
          <PhoneCall className="w-8 h-8 animate-pulse" />
        </div>
        <h1 className="text-2xl font-black text-[#1E4D3B] mt-4">Approve on your phone</h1>
        <p className="text-[#5C6660] mt-2">A payment prompt for <b>GH₵ {total.toFixed(2)}</b> has been sent to <b>{phone}</b> via <b>{prov.label}</b>. Enter your MoMo PIN to confirm.</p>
        <div className="mt-6 text-xs text-[#8C6D53] uppercase tracking-widest font-bold">Waiting for confirmation...</div>
        <div className="mt-6 flex justify-center">
          <div className="w-8 h-8 border-4 border-[#C85A32] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="text-3xl font-extrabold text-[#1E4D3B]">Checkout</h1>
      <div className="mt-4 flex items-start gap-3 rounded-xl border border-[#D99B26] bg-[#FFFBEE] p-4" data-testid="demo-payment-banner">
        <AlertTriangle className="w-5 h-5 text-[#D99B26] shrink-0 mt-0.5" />
        <div className="text-sm text-[#1F2421]">
          <div className="font-bold">TEST MODE — Paystack sandbox</div>
          <div className="text-[#5C6660]">Payments run against Paystack's <b>test environment</b> only. Use Paystack test cards or the test Mobile Money numbers from your dashboard — no real money moves.</div>
        </div>
      </div>
      <div className="grid md:grid-cols-[1fr_360px] gap-8 mt-6">
        <form onSubmit={submit} className="space-y-6">
          <div className="card-farm p-6">
            <div className="font-bold text-lg text-[#1E4D3B]">Delivery details</div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="text-xs uppercase font-bold tracking-widest text-[#8C6D53]">Full name</label>
                <input value={user.name} readOnly className="w-full mt-1 px-3 py-2 border border-[#E5D9C8] rounded-lg bg-[#F3ECE2] text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase font-bold tracking-widest text-[#8C6D53]">Region</label>
                <select value={region} onChange={(e) => setRegion(e.target.value)} className="w-full mt-1 px-3 py-2 border border-[#E5D9C8] rounded-lg text-sm bg-white" data-testid="checkout-region-select">
                  {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className="text-xs uppercase font-bold tracking-widest text-[#8C6D53]">Delivery address</label>
              <textarea required value={address} onChange={(e) => setAddress(e.target.value)} rows={3} placeholder="Street, town, landmark" className="w-full mt-1 px-3 py-2 border border-[#E5D9C8] rounded-lg text-sm" data-testid="checkout-address-input" />
            </div>
          </div>

          <div className="card-farm p-6">
            <div className="font-bold text-lg text-[#1E4D3B]">Mobile Money</div>
            <p className="text-sm text-[#5C6660]">Choose your MoMo network and enter your registered phone.</p>
            <div className="grid grid-cols-3 gap-3 mt-4">
              {PROVIDERS.map((p) => (
                <button key={p.key} type="button" onClick={() => setProvider(p.key)}
                  className={`p-3 rounded-xl font-bold text-sm border-2 transition ${provider === p.key ? "border-[#1E4D3B] shadow-md" : "border-transparent opacity-75"}`}
                  style={{ background: p.bg, color: p.text }}
                  data-testid={`momo-provider-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="mt-4">
              <label className="text-xs uppercase font-bold tracking-widest text-[#8C6D53]">MoMo phone number</label>
              <input required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="024XXXXXXX" className="w-full mt-1 px-3 py-2 border border-[#E5D9C8] rounded-lg text-sm" data-testid="checkout-phone-input" />
            </div>
          </div>

          <button type="submit" className="btn-primary w-full py-4 rounded-xl text-lg font-bold" data-testid="checkout-pay-button">
            Pay GH₵ {total.toFixed(2)} with MoMo
          </button>
        </form>

        <aside className="card-farm p-6 h-fit">
          <div className="text-xs uppercase tracking-widest font-bold text-[#8C6D53]">Your order</div>
          <div className="space-y-2 mt-3 text-sm">
            {items.map((it) => (
              <div key={it.product.id} className="flex justify-between">
                <span className="pr-2">{it.product.name} × {it.quantity}</span>
                <span className="font-semibold">GH₵ {((it.unit_price ?? it.product.price) * it.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="h-px bg-[#E5D9C8] my-4" />
          <div className="flex justify-between text-lg font-black text-[#1E4D3B]"><span>Total</span><span>GH₵ {total.toFixed(2)}</span></div>
        </aside>
      </div>
    </div>
  );
}
